import { useEffect, useMemo, useState } from 'react';
import type { AgentConversation, AgentKind, AutonomyLevel, ResumeMode, Workspace } from '@cm/protocol';
import { AUTONOMY_LABELS, AUTONOMY_RISK } from '@cm/protocol';
import { api } from '../api';
import { useStore } from '../store';
import { AgentMark } from './AgentMark';

interface Props {
  workspace: Workspace;
  onClose: () => void;
}

export function NewSessionDialog({ workspace, onClose }: Props) {
  const profiles = useStore((s) => s.profiles);
  const agents = useStore((s) => s.agents);
  const pushToast = useStore((s) => s.pushToast);
  const focus = useStore((s) => s.focus);

  const installed = useMemo(() => agents.filter((a) => a.installed), [agents]);

  const [profileId, setProfileId] = useState('');
  const [agent, setAgent] = useState<AgentKind>(installed[0]?.agent ?? 'claude');
  const [autonomy, setAutonomy] = useState<AutonomyLevel>('manual');
  const [title, setTitle] = useState('');
  const [model, setModel] = useState('');
  // Subpasta opcional dentro do projeto: útil para apontar um agente só para
  // o backend e outro só para o frontend do mesmo repositório.
  const [subdir, setSubdir] = useState('');
  const [initialPrompt, setInitialPrompt] = useState('');
  const [busy, setBusy] = useState(false);

  // Retomada de conversa
  const [resumeMode, setResumeMode] = useState<ResumeMode>('new');
  const [conversationId, setConversationId] = useState('');
  const [conversations, setConversations] = useState<AgentConversation[] | null>(null);
  const [loadingConvs, setLoadingConvs] = useState(false);

  // Escolher um perfil preenche o formulário com os valores dele — menos o
  // cwd, que pertence ao projeto.
  useEffect(() => {
    if (!profileId) return;
    const profile = profiles.find((p) => p.id === profileId);
    if (!profile) return;
    setAgent(profile.agent);
    setAutonomy(profile.autonomy);
    setModel(profile.model ?? '');
  }, [profileId, profiles]);

  // Memoizado porque entra num array de dependências: `?? []` criaria um
  // array novo a cada render e faria o efeito reexecutar sem necessidade.
  const levels = useMemo(
    () => agents.find((a) => a.agent === agent)?.supportedAutonomy ?? [],
    [agents, agent],
  );

  // Trocar de agente pode invalidar o nível escolhido (o Codex não tem plan).
  useEffect(() => {
    if (levels.length && !levels.includes(autonomy)) setAutonomy(levels[0]);
  }, [levels, autonomy]);

  const resumeCaps = useMemo(
    () => agents.find((a) => a.agent === agent)?.resume,
    [agents, agent],
  );

  const cwd = subdir.trim()
    ? `${workspace.cwd.replace(/\/+$/, '')}/${subdir.trim().replace(/^\/+/, '')}`
    : workspace.cwd;

  // Trocar de agente pode invalidar o modo escolhido (o Gemini so tem "a
  // ultima"), e a lista carregada pertencia ao agente antigo.
  useEffect(() => {
    setConversations(null);
    setConversationId('');
    setResumeMode((current) => {
      if (current === 'new') return current;
      if (current === 'last' && resumeCaps?.last) return current;
      if (current === 'id' && resumeCaps?.byId && resumeCaps?.listable) return current;
      if (current === 'picker' && resumeCaps?.picker) return current;
      return 'new';
    });
  }, [agent, resumeCaps]);

  // Busca a lista so quando ela vai ser usada: ler transcripts custa I/O, e a
  // maioria das sessoes comeca em conversa nova.
  useEffect(() => {
    if (resumeMode !== 'id' || conversations !== null) return;
    let cancelled = false;
    setLoadingConvs(true);
    api
      .conversations(agent, cwd)
      .then((res) => {
        if (!cancelled) setConversations(res.conversations);
      })
      .catch((err) => {
        if (cancelled) return;
        setConversations([]);
        pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
      })
      .finally(() => {
        if (!cancelled) setLoadingConvs(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resumeMode, conversations, agent, cwd, pushToast]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const { session, warnings } = await api.createSession({
        workspaceId: workspace.id,
        profileId: profileId || undefined,
        agent,
        autonomy,
        title: title.trim() || undefined,
        model: model.trim() || null,
        cwd,
        initialPrompt: initialPrompt.trim() || undefined,
        resumeMode,
        conversationId: resumeMode === 'id' ? conversationId : undefined,
      });
      focus(session.id);
      pushToast({ kind: 'success', text: `${session.title} iniciada` });
      for (const warning of warnings ?? []) pushToast({ kind: 'warn', text: warning });
      onClose();
    } catch (err) {
      pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  const risk = AUTONOMY_RISK[autonomy];

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <form className="modal" onMouseDown={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>
          Novo agente em{' '}
          <span style={{ color: workspace.color }}>{workspace.name}</span>
        </h2>

        <div className="field">
          <label>Agente</label>
          <div className="row wrap">
            {agents.map((a) => (
              <button
                key={a.agent}
                type="button"
                className={agent === a.agent ? 'primary' : ''}
                disabled={!a.installed}
                onClick={() => setAgent(a.agent)}
                title={a.installed ? a.label : `${a.label} não está instalado`}
                style={{ display: 'flex', alignItems: 'center', gap: 7 }}
              >
                <AgentMark agent={a.agent} size={14} />
                {a.label}
              </button>
            ))}
          </div>
        </div>

        <div className="two-col">
          <div className="field">
            <label htmlFor="autonomy">Nível de autonomia</label>
            <select
              id="autonomy"
              value={autonomy}
              onChange={(e) => setAutonomy(e.target.value as AutonomyLevel)}
            >
              {levels.map((level) => (
                <option key={level} value={level}>
                  {AUTONOMY_LABELS[level]}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="profile">Perfil salvo</label>
            <select id="profile" value={profileId} onChange={(e) => setProfileId(e.target.value)}>
              <option value="">— nenhum —</option>
              {profiles
                .filter((p) => p.agent === agent)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {risk >= 2 && (
          <div className={risk === 3 ? 'danger-box' : 'warn-box'}>
            {risk === 3
              ? 'YOLO executa comandos sem sandbox e sem confirmação. O daemon só aceita esse nível se tiver sido iniciado com CM_ALLOW_YOLO=1.'
              : 'Full-auto aprova comandos sem perguntar, mas mantém o sandbox de escrita no workspace.'}
          </div>
        )}

        <div className="two-col">
          <div className="field">
            <label htmlFor="title">Nome do pane</label>
            <input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="automático"
            />
          </div>
          <div className="field">
            <label htmlFor="model">Modelo</label>
            <input
              id="model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="padrão do CLI"
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="subdir">Subpasta (opcional)</label>
          <input
            id="subdir"
            value={subdir}
            onChange={(e) => setSubdir(e.target.value)}
            placeholder="ex: packages/api — deixe vazio para a raiz do projeto"
          />
          {/* Prefixo "→" e cor esmaecida para nao ser confundido com um
              segundo campo de texto: isto e o resultado, nao uma entrada. */}
          <div className="path-preview">
            <span className="arrow">→</span>
            <span>{cwd}</span>
          </div>
        </div>

        <div className="field">
          <label htmlFor="resume">Conversa</label>
          <select
            id="resume"
            value={resumeMode}
            onChange={(e) => setResumeMode(e.target.value as ResumeMode)}
          >
            <option value="new">Nova conversa</option>
            {resumeCaps?.last && <option value="last">Continuar a última desta pasta</option>}
            {resumeCaps?.byId && resumeCaps?.listable && (
              <option value="id">Escolher uma conversa…</option>
            )}
            {resumeCaps?.picker && <option value="picker">Abrir o seletor do agente</option>}
          </select>

          {resumeMode === 'picker' && (
            <p className="card-desc" style={{ marginTop: 7, marginBottom: 0 }}>
              O próprio agente abre a lista dentro do pane; você escolhe por lá.
            </p>
          )}

          {resumeMode === 'last' && !resumeCaps?.listable && (
            <p className="card-desc" style={{ marginTop: 7, marginBottom: 0 }}>
              Este agente não permite listar conversas, só continuar a mais recente.
            </p>
          )}
        </div>

        {resumeMode === 'id' && (
          <div className="field">
            {loadingConvs && <p className="card-desc">procurando conversas…</p>}

            {!loadingConvs && conversations?.length === 0 && (
              <p className="card-desc">
                Nenhuma conversa gravada nesta pasta ainda. Comece com uma conversa nova.
              </p>
            )}

            <div className="conv-list">
              {(conversations ?? []).map((c) => (
                <button
                  type="button"
                  key={c.id}
                  className={`conv-item${conversationId === c.id ? ' selected' : ''}`}
                  onClick={() => setConversationId(c.id)}
                >
                  <span className="conv-title">{c.title ?? '(sem título)'}</span>
                  <span className="conv-meta">
                    {formatWhen(c.updatedAt)} · {formatSize(c.sizeBytes)}
                    {c.gitBranch ? ` · ${c.gitBranch}` : ''}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="field">
          <label htmlFor="prompt">Prompt inicial (opcional)</label>
          <textarea
            id="prompt"
            value={initialPrompt}
            onChange={(e) => setInitialPrompt(e.target.value)}
            placeholder="Digitado no agente assim que a sessão terminar de subir."
          />
        </div>

        <div className="row end">
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="primary"
            type="submit"
            disabled={busy || (resumeMode === 'id' && !conversationId)}
          >
            {busy ? 'subindo…' : resumeMode === 'new' ? 'Abrir agente' : 'Retomar'}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Data curta e relativa: o que importa é "foi hoje ou foi em agosto". */
function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  if (days === 0) return `hoje ${date.toTimeString().slice(0, 5)}`;
  if (days === 1) return 'ontem';
  if (days < 30) return `${days} dias atrás`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}
